import React, { useEffect, useState } from "react";

import { DashboardFilterType } from "@/types/dashboard";

import {
  getDashboardBidFilterToString,
  getDashboardProjectFilterToString,
} from "@/consts/Dashboard";

import { showToast } from "@/utils/toastUtils";
import { cn, formatKoreanCurrencyWithWon } from "@/libs/utils";

import DashboardCardHeader from "@/components/DashboardCardHeader";
import LabelWithTooltip from "@/components/LabelWithTooltip";

type ChartDataItem = {
  label: string;
  value: number;
  count?: number;
};

const colorList = [
  "#6665DD",
  "#0496FF",
  "#FF715B",
  "#DE0D92",
  "#34D1BF",
  "#DDB965",
  "#FFE204",
  "#F75BFF",
  "#8A0808",
  "#349AD1",
];

type Props = {
  type: "bid" | "project";
  title: string;
  searchReady: boolean;
  method: any;
  filter: DashboardFilterType;
  label: string;
  value: string;
  onSearchComplete: () => void;
};

export default function DashboardHorizontalBarChart({
  type = "bid",
  title,
  searchReady,
  method,
  filter,
  label,
  value,
  onSearchComplete,
}: Props) {
  const isProject = type === "project";

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [data, setData] = useState<ChartDataItem[]>([]);
  const [maxValue, setMaxValue] = useState<number>(0);
  const onLoad = async () => {
    try {
      setIsLoading(true);
      const params = isProject
        ? getDashboardProjectFilterToString(filter)
        : getDashboardBidFilterToString(filter);
      const result = await method(params);
      if (result.data.success) {
        const newData = result.data.pods[0].content.items.map((item: any) => ({
          label: item[label],
          value: item[value],
          count: item.line_count,
        }));
        setMaxValue(Math.max(...newData.map((item: any) => item.value)));
        setData(newData);
      } else {
        showToast({
          type: "error",
          message: `${title}을(를) 조회하는데 실패했습니다. ${result.data.error.type}`,
        });
      }
    } catch (e) {
      showToast({
        type: "error",
        message: `${title}을(를) 조회하는데 실패했습니다. ${e}`,
      });
    } finally {
      setIsLoading(false);
      onSearchComplete();
    }
  };

  useEffect(() => {
    if (searchReady) {
      onLoad();
    }
  }, [searchReady]);
  return (
    <DashboardCardHeader title={title} isLoading={isLoading}>
      <div
        className={cn(
          "w-full max-w-2xl mx-auto px-10 bg-white rounded-xl flex flex-col gap-4",
          { "h-full flex justify-center items-center": data.length < 1 }
        )}
      >
        {data.length > 0 ? (
          data.map((item, index) => {
            const widthPercent = (item.value / maxValue) * 100;

            return (
              <div key={item.label}>
                <div className="flex justify-between text-sm">
                  <div className="flex items-center gap-1">
                    <LabelWithTooltip
                      className="text-sm text-gray-A font-semibold"
                      label={item.label}
                    />
                    {isProject && item.count !== undefined && (
                      <span className="text-[10px] font-semibold text-gray-B">
                        ({item.count})
                      </span>
                    )}
                  </div>
                  <span className="text-xs text-blue-B font-semibold">
                    {isProject
                      ? formatKoreanCurrencyWithWon(item.value)
                      : item.value.toLocaleString()}
                  </span>
                </div>
                <div className="relative h-2 mt-1 bg-gray-100 rounded-full">
                  <div
                    className="h-full rounded-full"
                    style={{
                      width: `${widthPercent}%`,
                      backgroundColor: colorList[index],
                    }}
                  />
                </div>
              </div>
            );
          })
        ) : (
          <div className="text-gray-B text-sm">
            해당 검색 조건으로 결과가 없습니다.
          </div>
        )}
      </div>
    </DashboardCardHeader>
  );
}
